import { Badge } from "reactstrap";
import { useSearchParams } from "react-router-dom";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import IconButton from "../../../../components/Buttons/IconButton";
import { IoCreateOutline, IoCopyOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { del, get, post } from "../../../../utils/helpers/api";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import DefaultPagination from "../../../../components/Pagination";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { toast } from "react-toastify";
import { AWS_S3_URL } from "../../../../utils/constants";
import { useTranslation } from "react-i18next";
import AgGridTable from "../../../../components/Tables/AgGrid";
import { TfiFilter } from "react-icons/tfi";
import FiltersDrawer from "./Drawers/Filters";
import useCurrency from "../../../../utils/helpers/currency";

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={() => context.cloneProduct(data.id)}
        size="sm" outline color="success"
      >
        <IoCopyOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate("/inventory/products/edit/" + data.id, { state: { productData: data } });
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setProductToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function ProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currency = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [productToDelete, setProductToDelete] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['name', 'category', 'type', 'min_selling_price', 'max_selling_price', 'min_cost_price', 'max_cost_price', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(Boolean).length;

  const getProducts = useCallback(async () => {
    setLoading(true);
    let query = '/inventory/products?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }

    const data = await get(query);
    if(data?.success) {
      setRowData(data.products.data);
      setTotalPages(data.products.last_page);
      setTotalRecords(data.products.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  const cloneProduct = async (productId) => {
    const data = await post('/inventory/products/clone/' + productId);
    if (data?.success) {
      toast.success(data.message || t('inventory.productCloned'));
      navigate('/inventory/products/edit/' + data.product.id, { state: {productData: data.product} })
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
  };

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  const deleteProduct = async () => {
    const data = await del('/inventory/products/delete/' + productToDelete);
    if (data?.success) {
      toast.success(data.message || t('inventory.productDeleted'));
      getProducts();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setProductToDelete(null);
  };

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: t('common.name'),
      flex: 1,
      minWidth: 240,
      sortable: true,
      cellRenderer: (params) => {
        return (
          <div className="d-flex align-items-center gap-2">
            <img
              src={params.data.image_path ? AWS_S3_URL + params.data.image_path : 'https://placehold.co/40'}
              alt={params.value}
              style={{ objectFit: 'cover', borderRadius: 4 }}
              width={32}
              height={32}
              className="border"
            />
            {params.value}
          </div>
        );
      }
    },
    {
      field: 'category.name',
      headerName: t('inventory.category'),
      flex: 1,
      minWidth: 130,
      sortable: true
    },
    { field: 'sku', headerName: t('inventory.sku'), flex: 1, minWidth: 130, sortable: true },
    { 
      field: 'cost_price', 
      headerName: t('inventory.costPrice'), 
      flex: 1, 
      width: 130, 
      sortable: true,
      valueFormatter: (params) => {
        return params.value + ' ' + currency
      }
    },
    { 
      field: 'selling_price', 
      headerName: t('inventory.sellingPrice'), 
      flex: 1, 
      width: 130, 
      sortable: true,
      valueFormatter: (params) => {
        return params.value + ' ' + currency
      }
    },
    { field: 'current_stock', headerName: t('inventory.stock'), flex: 1, width: 100, sortable: true },
    { headerName: t('common.actions'), width: 100, flex: 1, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer }
  ], [t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setProductToDelete, cloneProduct }), [navigate]);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    navigate(`/inventory/products/view/${event.data.id}`, { state: { productData: event.data } });
  };

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('inventory.products')} onReload={getProducts} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/inventory/products/create')}>
            {t('inventory.newProduct')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={productToDelete !== null}
        title={t('inventory.deleteProduct')}
        message={t('inventory.deleteProductConfirm')}
        onConfirm={deleteProduct}
        toggle={() => setProductToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default ProductsPage;